import Box from '@mui/material/Box';

import { AppButton, AppCard } from '@/components/atoms';
import { useVehicleCardModel } from '@/hooks/useVehicleCardModel';
import { type VehicleListing } from '@/services/vehicles/types';

import { VehicleCardHeading } from './VehicleCardHeading';
import { VehicleCardMedia } from './VehicleCardMedia';
import { VehiclePrice } from './VehiclePrice';
import { VehicleSpecLine } from './VehicleSpecLine';

type Props = { vehicle: VehicleListing; headingLevel?: 'h2' | 'h3' };

/**
 * The listing card (§17).
 *
 * It is an entry point to the car's page, not a substitute for it: one
 * written call to action, with share, add to cart and WhatsApp as icons on
 * the photograph. The four stacked buttons this replaced were four things to
 * read before anyone could compare two cars.
 *
 * The card is a layout and nothing else — what to show comes from
 * `useVehicleCardModel`, cart state from `useCarOrderEntry` inside
 * the icon button. The body is a four-row grid whose third row absorbs the
 * slack, so the price and the button sit on the same line across a row of
 * cards however short a car's details are.
 */
export function VehicleCard({ vehicle, headingLevel = 'h3' }: Props) {
  const { title, path, subtitle, specs, whatsappMessage, isSold } = useVehicleCardModel(vehicle);

  return (
    <AppCard
      component="article"
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'transform 260ms ease, box-shadow 260ms ease, border-color 260ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: 'primary.main',
          boxShadow: '0 18px 40px -20px rgba(11, 11, 12, 0.45)',
        },
        // The photo zoom is driven from the card so it also answers to a
        // keyboard focus landing anywhere inside it.
        '&:hover .vehicle-card__photo, &:focus-within .vehicle-card__photo': {
          transform: 'scale(1.06)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
        ...(isSold && { opacity: 0.72 }),
      }}
    >
      <VehicleCardMedia
        vehicle={vehicle}
        title={title}
        whatsappMessage={whatsappMessage}
        isSold={isSold}
      />

      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr auto',
          gap: 3,
          p: { xs: 4, md: 5 },
        }}
      >
        <VehicleCardHeading
          title={title}
          subtitle={subtitle}
          to={path}
          headingLevel={headingLevel}
        />
        <VehicleSpecLine specs={specs} />
        <Box sx={{ alignSelf: 'end' }}>
          <VehiclePrice vehicle={vehicle} />
        </Box>
        {/* Decorative: the stretched title link above already makes the whole
            card operable, so this must not become a second tab stop to the
            same page. */}
        <AppButton to={path} fullWidth tabIndex={-1} aria-hidden>
          View Details
        </AppButton>
      </Box>
    </AppCard>
  );
}
